import React, { useEffect, useRef, useState } from 'react';

import { PillButton } from 'ds';
import { SERVICES_REQUEST_PATH } from 'routes';
import { ReactComponent as CodiCLogo } from 'ux/Layouts/Shared/Navigation/Codi-C-Logo.svg';

import './DayOne.scss';

const DOCS = [
  { emoji: '📄', label: 'Lease agreement', delay: 0 },
  { emoji: '🛡️', label: 'Insurance & COI', delay: 180 },
  { emoji: '🏢', label: 'Building contacts', delay: 360 },
  { emoji: '📦', label: 'Vendor instructions', delay: 540 }
];

const STEPS = [
  { done: true, text: 'kitchen sink leak reported' },
  { done: true, text: "tenant's responsibility confirmed" },
  { done: true, text: 'approved plumber found' },
  { done: false, text: 'scheduling for Tuesday…' }
];

export default function DayOne() {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(-1);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  // Step-by-step reveal once visible
  useEffect(() => {
    if (!visible) return;
    let i = 0;
    const tick = () => {
      setActiveStep(i);
      i++;
      if (i < STEPS.length) setTimeout(tick, 900);
    };
    const t = setTimeout(tick, 800);
    return () => clearTimeout(t);
  }, [visible]);

  return (
    <section className="day-one" id="intel" ref={sectionRef}>
      {/* ── Text ── */}
      <div className={`day-one__copy ${visible ? 'day-one__copy--visible' : ''}`}>
        <h2 className="day-one__heading">
          Up and running
          <br />
          <span className="day-one__accent">on day one.</span>
        </h2>
        <p className="day-one__sub">
          Upload your office data once. Codi has the context it needs to operate your office with precision.
        </p>
        <PillButton
          type="purpleGradient"
          size="lg"
          text="Get started"
          href={SERVICES_REQUEST_PATH}
          icon="rightArrow"
          iconPosition="right"
          hasShadow
        />
      </div>

      {/* ── Illustration ── */}
      <div className="day-one__illustration">
        {/* Left: doc chips flying in */}
        <div className="day-one__docs">
          {DOCS.map((doc, i) => (
            <div
              key={i}
              className={`day-one__doc ${visible ? 'day-one__doc--visible' : ''}`}
              style={{ transitionDelay: `${doc.delay}ms` }}
            >
              <span className="day-one__doc-emoji">{doc.emoji}</span>
              <span className="day-one__doc-label">{doc.label}</span>
              <span className="day-one__doc-badge">Read</span>
            </div>
          ))}
        </div>

        {/* Center: connector + Codi core */}
        <div className={`day-one__core-col ${visible ? 'day-one__core-col--visible' : ''}`}>
          {/* Line coming IN from right (docs → Codi) */}
          <div className="day-one__line day-one__line--in">
            <span className="day-one__line-track" />
          </div>

          {/* Codi logo */}
          <div className="day-one__core">
            <div className="day-one__core-ring day-one__core-ring--outer" />
            <div className="day-one__core-ring day-one__core-ring--inner" />
            <div className="day-one__core-logo" role="img" aria-label="Codi">
              <CodiCLogo className="day-one__core-logoSvg" width={20} height={19} />
            </div>
          </div>

          {/* Line going OUT to left (Codi → steps) */}
          <div className="day-one__line day-one__line--out">
            <span className="day-one__line-track" />
          </div>
        </div>

        {/* Right: live reasoning steps */}
        <div className={`day-one__steps ${visible ? 'day-one__steps--visible' : ''}`}>
          <div className="day-one__steps-header">
            <span className="day-one__steps-dot" />
            Codi is working
          </div>
          <div className="day-one__steps-list">
            {STEPS.map((step, i) => (
              <div
                key={i}
                className={`day-one__step ${i <= activeStep ? 'day-one__step--visible' : ''} ${step.done && i <= activeStep ? 'day-one__step--done' : ''} ${!step.done && i === activeStep ? 'day-one__step--active' : ''}`}
              >
                <span className="day-one__step-icon">
                  {step.done && i <= activeStep ? (
                    <svg viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 6l2.5 2.5L10 3.5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <span className="day-one__step-spinner" />
                  )}
                </span>
                <span className="day-one__step-text">{step.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
